import { MutationStatus } from '@tanstack/react-query';
import { ConfirmSignInOutput } from 'aws-amplify/auth';
import React, { Dispatch, ReactNode, SetStateAction } from 'react';

import { TagSeverity } from '@/components/chariot-ui';
import { InputsT } from '@/components/form/Inputs';

// Global constants injected at build time via Vite
declare global {
  const __APP_VERSION__: string;
  const __BUILD_TIMESTAMP__: number;
  const __GIT_COMMIT__: string;
}

export const JOBS_CRON = '0 0 * * *';

export interface ValidationCredentials {
  [key: string]: string;
}

export interface IntegrationValidationError {
  code: string;
  title: string;
  message: string;
  field?: string;
  hint?: string; // Actionable guidance for the user
}

export interface ValidateIntegrationErrorResponse {
  valid: boolean;
  errors?: IntegrationValidationError[];
  templates?: {
    [key: string]: {
      error: string;
      missingPermissions: string[];
      projectKey: string;
      projectName: string;
    };
  };
}

export enum ResourceType {
  Asset = 'asset',
  Risk = 'risk',
  Attribute = 'attribute',
  Port = 'port',
  Preseed = 'preseed',
  Seed = 'seed',
  Account = 'account',
  Job = 'job',
  Technology = 'technology',
  Webpage = 'webpage',
  WebApplication = 'webapplication',
}

export enum UniqueQueryKeys {
  Backends = 'backends',
  ACCOUNT_ALERTS = 'ACCOUNT_ALERTS',
  MY = 'MY',
  GENERIC_MY_SEARCH = 'GENERIC_MY_SEARCH',
  GET_FILE = 'GET_FILE',
  COUNTS = 'COUNTS',
  GAVATAR_PROFILE_PICTURE = 'GAVATAR_PROFILE_PICTURE',
  KEV = 'KEV',
  USER_ATTRIBUTES = 'USER_ATTRIBUTES',
  SEED_FILTERS = 'SEED_FILTERS',
  THREAT_INTEL = 'THREAT_INTEL',
  THREAT_INTEL_DETAILS = 'THREAT_INTEL_DETAILS',
  ENRICH = 'ENRICH',
  GRAPH_QUERY = 'GRAPH_QUERY',
  AGENTS = 'AGENTS',
  AEGIS_MANAGEMENT_TASKS = 'AEGIS_MANAGEMENT_TASKS',
  CAPABILITIES = 'CAPABILITIES',
  API_KEYS = 'API_KEYS',
  PLEXTAC_REPORTS = 'PLEXTAC_REPORTS',
  EVIDENCE_FILES = 'EVIDENCE_FILES',
  REPORTING = 'REPORTING',
  COLLABORATOR_QUERY = 'COLLABORATOR_QUERY',
  PLEXTAC_CLIENT_ID = 'PLEXTAC_CLIENT_ID',
  SSO_ID = 'SSO_ID',
}

/* Graph Query Types */

/**
 * Source of truth for all static relationships
 * Includes Chariot core relationships and Active Directory relationships
 *
 * Types are derived from this array - add new relationships here only
 */
export const STATIC_RELATIONSHIPS = [
  // Chariot
  'DISCOVERED',
  'HAS_VULNERABILITY',
  'HAS_ATTRIBUTE',
  'HAS_PORT',
  'HAS_TECHNOLOGY',
  'HAS_WEBPAGE',
  'INSTANCE_OF',
  // Active Directory
  'Owns',
  'GenericAll',
  'GenericWrite',
  'WriteOwner',
  'WriteDacl',
  'MemberOf',
  'ForceChangePassword',
  'AllExtendedRights',
  'AddMember',
  'HasSession',
  'Contains',
  'GPLink',
  'AllowedToDelegate',
  'CoerceToTGT',
  'GetChanges',
  'GetChangesAll',
  'GetChangesInFilteredSet',
  'CrossForestTrust',
  'SameForestTrust',
  'SpoofSIDHistory',
  'AbuseTGTDelegation',
  'AllowedToAct',
  'AdminTo',
  'CanPSRemote',
  'CanRDP',
  'ExecuteDCOM',
  'HasSIDHistory',
  'AddSelf',
  'DCSync',
  'ReadLAPSPassword',
  'ReadGMSAPassword',
  'DumpSMSAPassword',
  'SQLAdmin',
  'AddAllowedToAct',
  'WriteSPN',
  'AddKeyCredentialLink',
  'LocalToComputer',
  'MemberOfLocalGroup',
  'RemoteInteractiveLogonRight',
  'SyncLAPSPassword',
  'WriteAccountRestrictions',
  'WriteGPLink',
  'RootCAFor',
  'DCFor',
  'PublishedTo',
  'ManageCertificates',
  'ManageCA',
  'DelegatedEnrollmentAgent',
  'Enroll',
  'HostsCAService',
  'WritePKIEnrollmentFlag',
  'WritePKINameFlag',
  'NTAuthStoreFor',
  'TrustedForNTAuth',
  'EnterpriseCAFor',
  'IssuedSignedBy',
  'GoldenCert',
  'EnrollOnBehalfOf',
  'OIDGroupLink',
  'ExtendedByPolicy',
  'ADCSESC1',
  'ADCSESC3',
  'ADCSESC4',
  'ADCSESC6a',
  'ADCSESC6b',
  'ADCSESC9a',
  'ADCSESC9b',
  'ADCSESC10a',
  'ADCSESC10b',
  'ADCSESC13',
  'SyncedToEntraUser',
  'CoerceAndRelayNTLMToSMB',
  'CoerceAndRelayNTLMToADCS',
  'CoerceAndRelayNTLMToLDAP',
  'CoerceAndRelayNTLMToLDAPS',
  'WriteOwnerLimitedRights',
  'WriteOwnerRaw',
  'OwnsLimitedRights',
  'OwnsRaw',
  'ClaimSpecialIdentity',
  'ContainsIdentity',
  'PropagatesACEsTo',
  'GPOAppliesTo',
  'CanApplyGPO',
  'HasTrustKeys',
] as const;

/**
 * Source of truth for cloud relationship prefixes
 * Used for AWS/cloud service relationships (STS, IAM, Lambda, etc.)
 *
 * Types are derived from this array - add new prefixes here only
 */
export const RELATIONSHIP_PREFIXES = ['STS_', 'IAM_', 'LAMBDA_'] as const;

/**
 * Static relationships with fixed string values
 * Derived from STATIC_RELATIONSHIPS array in utils/validation.ts
 *
 * To add a new relationship, update the STATIC_RELATIONSHIPS array - not this type
 */
export type StaticRelationship = (typeof STATIC_RELATIONSHIPS)[number];

/**
 * Cloud relationships with dynamic prefixes
 * Derived from RELATIONSHIP_PREFIXES array in utils/validation.ts
 *
 * To add a new prefix, update the RELATIONSHIP_PREFIXES array - not this type
 */
export type PrefixRelationship =
  `${(typeof RELATIONSHIP_PREFIXES)[number]}${string}`;

/**
 * Complete relationship type union
 * Combines static and prefix-based relationships
 *
 * Single source of truth: utils/validation.ts exports the arrays
 */
export type Relationship = StaticRelationship | PrefixRelationship;

/**
 * Source of truth for all node labels
 * Type is derived from this array - add new labels here only
 */
export const NODE_LABEL_VALUES = [
  'Asset',
  'Repository',
  'Attribute',
  'Port',
  'Risk',
  'Account',
  'Preseed',
  'Seed',
  'TTL',
  'Technology',
  'Vulnerability',
  'Webpage',
  'WebApplication',
  'Target',
  'ADDomain',
  'ADObject',
  'ADUser',
  'ADComputer',
  'ADGroup',
  'ADGPO',
  'ADOU',
  'ADContainer',
  'ADLocalGroup',
  'ADLocalUser',
  'ADAIACA',
  'ADRootCA',
  'ADEnterpriseCA',
  'ADNTAuthStore',
  'ADCertTemplate',
  'ADIssuancePolicy',
  'ADRelationship',
  'CloudResource',
  'Credential',
  'Generic',
  'ParkedDomain',
  'AWSResource',
  'GCPResource',
  'AzureResource',
  'MonitoringSession',
  'MonitoredTechnique',
  'MonitorDetection',
] as const;

/**
 * Node label type derived from constant array
 * Single source of truth: NODE_LABEL_VALUES array
 */
export type NodeLabel = (typeof NODE_LABEL_VALUES)[number];

export type LabelOperator = 'OR' | 'AND' | '';

export type Operator =
  | '='
  | '<>'
  | '<'
  | '<='
  | '>'
  | '>='
  | 'LIKE'
  | 'CONTAINS'
  | 'STARTS WITH'
  | 'ENDS WITH'
  | 'IN'
  | 'IS NULL'
  | 'IS NOT NULL'
  | 'OR'
  | 'AND'
  | 'ANY_STARTS_WITH'
  | 'ANY_IN';

export interface QueryFilter {
  field: string;
  operator: Operator;
  value:
    | string
    | number
    | boolean
    | (string | number | boolean)[]
    | (string | number | boolean)[][] // Support for nested arrays (e.g., IN operator with [["P"]])
    | NodeDetails['filters'];
  not?: boolean;
  reverseOperands?: boolean;
  alias?: string;
  relationshipNodeLabel?: string;
  relationshipDirection?: 'source' | 'target';
  relationshipLabel?: string;
}

// Alias for backward compatibility
export type Filter = QueryFilter;

export interface GraphQuery {
  node: NodeDetails;
  page?: number;
  limit?: number;
  orderBy?: string;
  descending?: boolean;
  orderByFields?: {
    field: string;
    descending: boolean;
    caseExpressionType?: string;
  }[];
  filters?: QueryFilter[];
  shortest?: number;
  usernames?: string[]; // Array of user emails to scope query
  groupBy?: string[]; // Fields to group by (for vulnerability grouping)
  aggregations?: { function: string; field: string }[]; // Aggregation functions (min, max, count, etc.)
}

export interface NodeDetails {
  labels?: NodeLabel[];
  label_operator?: LabelOperator;
  sorted?: boolean;
  filters?: QueryFilter[];
  search?: string; // Fulltext search string (Lucene-indexed, min 2 chars)
  relationships?: {
    label: Relationship | Relationship[];
    label_operator?: LabelOperator;
    filters?: QueryFilter[];
    length?: number;
    source?: NodeDetails;
    target?: NodeDetails;
    optional?: boolean;
    select_one?: boolean;
    not_exists?: boolean;
    self_edges?: 'forbid' | 'allow' | 'require';
  }[];
  self_edges?: 'forbid' | 'allow' | 'require';
  alias?: string;
}

export interface RelationshipData {
  created: string;
  visited: string;
  capability: string;
  key: string;
}

export interface RelationshipNode {
  result: {
    discovereds: RelationshipData[];
    hasports: RelationshipData[];
    hasattributes: RelationshipData[];
    haswebpages: RelationshipData[];
    count: number;
  };
  source: {
    result: GraphQueryResponse;
  };
  label: Relationship;
  target: TreeNode['node'];
}

export interface TreeNode {
  node: {
    result: GraphQueryResponse;
    relationships: RelationshipNode[];
  };
}
/* ------------------------------------------------------------ */

export interface GraphQueryResponse {
  assets?: Asset[];
  attributes?: Attribute[];
  ports?: Port[];
  preseeds?: Preseed[];
  seeds?: Record<string, unknown>;
  count?: number;
  relationships?: RelationshipNode[];
  integrations?: Asset[];
  repositories?: Asset[];
  webpages?: Webpage[];
  webapplications?: Asset[];
  risks?: Risk[];
  technologies?: Technology[];
  vulnerabilities?: Vulnerability[];
  adobjects?: ADObject[];
  generics?: Asset[];
  awsresources?: AWSResource[];
  monitoringsessions?: import('./sections/validations/types').MonitoringSession[];
  monitoredtechniques?: import('./sections/validations/types').MonitoredTechnique[];
  monitordetections?: import('./sections/validations/types').MonitorDetection[];
}

/**
 * AWS Resource entity from backend
 * Represents cloud resources from AWS (EC2, S3, etc.)
 */
export interface AWSResource {
  key: string; // Format: "#awsresource#accountRef#name"
  resourceType: string; // CloudResourceType (AWSEC2Instance, AWSS3Bucket, etc.)
  displayName: string; // Human-readable name
  name: string; // Full ARN
  identifier: string; // Resource identifier
  accountRef: string; // AWS account reference
  region?: string; // AWS region
  provider: string; // Always "aws"
  properties?: Record<string, unknown>; // Resource-specific properties
  status?: string; // Resource status
  created?: string; // First seen timestamp (RFC3339)
  visited?: string; // Last seen timestamp (RFC3339)
  attackSurface?: string[]; // Always includes "cloud"
  origins?: string[]; // Always includes "amazon"
}
/* ------------------------------------------------------------ */

export enum JobStatus {
  Queued = 'JQ',
  Running = 'JR',
  Fail = 'JF',
  Pass = 'JP',
  AsyncRunning = 'JRA',
  AsyncReturning = 'JQA',
  Partial = 'JX', // Frontend-only: mixed pass/fail results
}

export const JobStatusLabel = {
  [JobStatus.Pass]: 'Success',
  [JobStatus.Fail]: 'Failed',
  [JobStatus.Running]: 'Running',
  [JobStatus.Queued]: 'Queued',
  [JobStatus.AsyncRunning]: 'Async Running',
  [JobStatus.AsyncReturning]: 'Async Returning',
  [JobStatus.Partial]: 'Partial',
};

export enum RiskStatus {
  Triaged = 'T',
  Opened = 'O',
  Remediated = 'R',
  Accepted = 'I',
  Deleted = 'D',
}

export enum RiskDeletedSubStatus {
  FalsePositive = 'F',
  OutOfScope = 'S',
  Duplicate = 'D',
  Other = 'O',
}

export enum RiskSeverity {
  'Exposure' = 'E',
  'Info' = 'I',
  'Low' = 'L',
  'Medium' = 'M',
  'High' = 'H',
  'Critical' = 'C',
}

export const RiskPriority: Record<RiskSeverity, number> = {
  [RiskSeverity.Critical]: 0,
  [RiskSeverity.High]: 10,
  [RiskSeverity.Medium]: 20,
  [RiskSeverity.Low]: 30,
  [RiskSeverity.Info]: 40,
  [RiskSeverity.Exposure]: 50,
};

// Creates a type to combine the risk status and severity
export type RiskStatusSeverityKey = `${RiskStatus}${RiskSeverity}`;
export type RiskCombinedStatusCount = Partial<
  Record<RiskStatusSeverityKey, number>
>;

export const TagSeverityToRiskSeverityMap: Record<TagSeverity, RiskSeverity> = {
  critical: RiskSeverity.Critical,
  high: RiskSeverity.High,
  medium: RiskSeverity.Medium,
  low: RiskSeverity.Low,
  info: RiskSeverity.Info,
  exposure: RiskSeverity.Exposure,
};

export const RiskStatusWithoutSeverity = [RiskStatus.Deleted];

export type RiskCombinedStatus = string;

export enum AssetStatus {
  ActivePassive = 'AP',
  ActiveLow = 'AL',
  Active = 'A',
  Frozen = 'F',
  Deleted = 'D',
  Pending = 'P',
}

export const AssetStatusLabel: Record<AssetStatus, string> = {
  [AssetStatus.ActivePassive]: 'Passive Asset Enumeration',
  [AssetStatus.ActiveLow]: 'Active Asset Enumeration',
  [AssetStatus.Active]: 'Vulnerability Discovery',
  [AssetStatus.Frozen]: 'Freeze Asset',
  [AssetStatus.Deleted]: 'Delete Asset',
  [AssetStatus.Pending]: 'Pending Asset',
};

export const RiskStatusLabel: Record<RiskStatus, string> = {
  T: 'Pending',
  O: 'Open',
  R: 'Closed',
  I: 'Accepted',
  D: 'Rejected',
};

export const SeverityDef: Record<RiskSeverity, string> = {
  I: 'Info',
  L: 'Low',
  M: 'Medium',
  H: 'High',
  C: 'Critical',
  E: 'Exposure',
};

interface AccountTemplate {
  username: string;
  key: string;
  updated: string;
  settings?: {
    displayName?: string;
    pulledName?: string;
    templates?: ValidationCredentials[];
    severityThreshold?: string;
    searchTerm?: string;
    default_sso_role?: string;
    role_claim?: string;
  };
}

export interface Account extends AccountTemplate {
  name: string;
  member: string;
  config: AccountMetadata;
  displayName?: string;
  value?: string;
  role?: string;
  type?: string;
}

export interface LinkAccount {
  username: string;
  member?: string;
  config: AccountMetadata;
  value?: string;
  type?: string;
  key?: string;
  role?: string;
  settings?: {
    displayName?: string;
    templates?: (ValidationCredentials & {
      transitions?: JiraTransitionConfig;
    })[];
    severityThreshold?: string;
    searchTerm?: string;
    default_sso_role?: string;
    role_claim?: string;
    api_usage?: {
      limit: number;
      headroom?: number;
    };
  };
}

export interface AccountMetadata {
  displayName?: string;
  setting?: string;
  pin?: string;
  [key: string]: AccountMetadata | string | undefined;
}

export enum SeedStatus {
  PendingLow = 'PL',
  Pending = 'P',
  Accept = 'A',
  AcceptLow = 'AL',
  ActivePassive = 'AP',
  Reject = 'FR',
  Freeze = 'F',
  Delete = 'D',
}

export const SeedStatusLabel: Record<SeedStatus, string> = {
  // Reason for naming PendingLow the same as Pending:
  // 1. "Rename Pending Low to Pending." -pk
  // 2. "but for now we should just have pending vs pending low/pending high" - ns
  [SeedStatus.PendingLow]: 'Pending',
  [SeedStatus.Pending]: 'Pending',
  [SeedStatus.Accept]: 'Approved',
  [SeedStatus.AcceptLow]: 'Approved',
  [SeedStatus.ActivePassive]: 'Active Passive',
  [SeedStatus.Reject]: 'Rejected',
  [SeedStatus.Freeze]: 'Frozen',
  [SeedStatus.Delete]: 'Deleted',
};

export enum AddSeedType {
  AD_DOMAIN_SEED = 'addomainseed',
  DOMAIN_SEED = 'domainseed',
  CIDR_SEED = 'cidrseed',
  IP_SEED = 'ipseed',
  IMPORT_FILE = 'importFile',
}

export interface Seed {
  // Required fields from struct
  username: string;
  key: string;
  group: string;
  identifier: string;
  status: string;
  source: string;
  class: string;
  origin: string;
  origins?: string[];

  created: string;
  visited?: string;
  history?: EntityHistory[];
  comment?: string;

  // Optional fields (marked with pointers in Go)
  name?: string | null;
  country?: string | null;
  email?: string | null;
  registrar?: string | null;
  registrant?: string | null;
  tags?: string[];
  profile?: string;
  details_path?: string;
}

export type AssetSubtype = 'Network' | 'ActiveDirectory';
export interface Asset {
  asname: string;
  asnumber: string;
  asrange: string;
  attackSurface?: string[];
  capability?: string[];
  /** @deprecated Field will be removed in future version. Use other asset properties for classification. */
  class?: string;
  cloudAccount?: string;
  cloudId?: string;
  cloudService?: string;
  comment: string;
  config: unknown;
  country: string;
  created: string;
  distinguishedname?: string;
  dns: string;
  group: string;
  history?: EntityHistory[];
  identifier: string;
  key: string;
  label?: string;
  name: string;
  org?: string;
  private: boolean;
  profile?: string;
  secret?: string;
  source: string;
  status: AssetStatus;
  tags?: string[];
  ttl: number;
  url?: string;
  username: string;
  visited: string;
  value?: string;
  details_path?: string;
  origins?: string[]; // Computed field for table compatibility
  port?: string; // Port information from attributes (deprecated - use ports array)
  protocol?: string; // Protocol information from attributes (deprecated - use ports array)
  attributes?: Attribute[]; // Associated attributes for port/protocol data (deprecated)
  ports?: Port[]; // Structured Port models from HAS_PORT relationships (PR #3413)
  metadata?: {
    origins?: string[];
    capability?: string[];
    attackSurface?: string[];
    asnumber?: string;
    asname?: string;
    asrange?: string;
    country?: string;
    province?: string;
    city?: string;
    purchased?: string;
    updated?: string;
    expiration?: string;
    registrant?: string;
    registrar?: string;
    email?: string;
  };
}

export interface SMTPUser {
  email: string;
  username: string;
  password: string;
  created: string;
}

export interface ParkedDomain {
  key: string;
  username: string;
  domain: string;
  auto_renew: boolean;
  cloudflare_status: string;
  status: string;
  registrar: string;
  expires: string;
  registered: string;
  zone_id: string;
  checkout_start?: string;
  checkout_end?: string;
  checkout_user?: string;
  checkout_note?: string;
  parked_category: string;
  created: string;
  updated: string;
  comment: string;
  history: EntityHistory[];
  smtp_users?: SMTPUser[];
}

export interface ADObject extends Asset {
  label: string; // Required for ADObjects (overrides optional from Asset)
  domain: string;
  objectid: string;
  sid?: string;

  // Complete ADProperties mapping from backend model (using actual backend lowercase names)
  // Core AD Properties - Identity & Classification
  name: string; // Override Asset's required name field
  description?: string;
  displayname?: string; // Backend uses lowercase
  isdeleted?: boolean; // Backend uses lowercase
  dnshostname?: string; // Backend uses lowercase
  objectguid?: string; // Backend uses lowercase
  domainfqdn?: string; // Backend uses lowercase
  domainsid?: string; // Backend uses lowercase
  netbios?: string; // Backend uses lowercase
  distinguishedname?: string; // Backend uses lowercase
  samaccountname?: string; // Backend uses lowercase
  department?: string;

  // Security Context Properties
  adminCount?: boolean;
  sensitive?: boolean;
  lastLogon?: number;
  lastLogonTimestamp?: number;
  hasLaps?: boolean;
  dontRequirePreAuth?: boolean;
  passwordNeverExpires?: boolean;
  passwordNotRequired?: boolean;
  smartcardRequired?: boolean;
  passwordStoredUsingReversibleEncryption?: boolean;
  userCannotChangePassword?: boolean;
  passwordExpired?: boolean;
  lockedOut?: boolean;
  trustedToAuth?: boolean;
  unconstrainedDelegation?: boolean;
  logonType?: string;
  userAccountControl?: number;
  adminSdHolderProtected?: string;

  // Access Rights & Permissions Properties
  hasSpn?: boolean;
  homeDirectory?: string;
  logonScriptEnabled?: boolean;
  useDESKeyOnly?: boolean;
  functionalLevel?: string;
  trustType?: string;
  spoofSidHistoryBlocked?: string;
  ownerSid?: string;
  isAcl?: string;
  isAclProtected?: boolean;
  doesAnyAceGrantOwnerRights?: boolean;
  doesAnyInheritedAceGrantOwnerRights?: boolean;

  // Group Membership Properties
  isPrimaryGroup?: string;
  groupScope?: string;
  blocksInheritance?: boolean;
  inheritanceHash?: string;
  inheritanceHashes?: string;
  enforced?: string;
  groupLinkId?: string;

  // Certificate & CA Properties
  caSecurityCollected?: boolean;
  caName?: string;
  certChain?: string[];
  certName?: string;
  certThumbprint?: string;
  certThumbprints?: string[];
  hasEnrollmentAgentRestrictions?: string;
  enrollmentAgentRestrictionsCollected?: boolean;
  isUserSpecifiesSanEnabled?: string;
  isUserSpecifiesSanEnabledCollected?: boolean;
  roleSeparationEnabled?: string;
  roleSeparationEnabledCollected?: boolean;
  hasBasicConstraints?: boolean;
  basicConstraintPathLength?: number;
  unresolvedPublishedTemplates?: string[];
  crossCertificatePair?: string[];
  hasCrossCertificatePair?: boolean;

  // Certificate Template Properties (using backend lowercase names)
  ekus?: string[];
  subjectaltrequireupn?: boolean;
  subjectaltrequiredns?: boolean;
  subjectaltrequiredomaindns?: boolean;
  subjectaltrequireemail?: boolean;
  subjectaltrequirespn?: boolean;
  subjectrequireemail?: boolean;
  authorizedsignatures?: number;
  applicationpolicies?: string[];
  issuancepolicies?: string[];
  schemaversion?: number;
  requiresmanagerapproval?: boolean;
  authenticationenabled?: boolean;
  schannelauthenticationenabled?: boolean;
  enrolleesuppliessubject?: boolean;
  certificateapplicationpolicy?: string[];
  certificatenameflag?: string;
  effectiveekus?: string[];
  enrollmentflag?: string;
  flags?: string;
  nosecurityextension?: boolean;
  renewalperiod?: string;
  validityperiod?: string;
  oid?: string;
  certificatepolicy?: string[];
  certtemplateoid?: string;
  isaclprotected?: boolean;

  // Domain & Password Policy Properties
  expirePasswordsOnSmartCardOnlyAccounts?: boolean;
  machineAccountQuota?: number;
  supportedKerberosEncryptionTypes?: string[];
  tgtDelegation?: string;
  dsHeuristics?: string;
  trustAttributesInbound?: string;
  trustAttributesOutbound?: string;
  minPwdLength?: number;
  pwdProperties?: number;
  pwdHistoryLength?: number;
  lockoutThreshold?: number;
  minPwdAge?: string;
  maxPwdAge?: string;
  lockoutDuration?: string;
  lockoutObservationWindow?: number;

  // Network & Security Properties
  smbSigning?: boolean;
  webClientRunning?: boolean;
  restrictOutboundNtlm?: boolean;
  gmsa?: boolean;
  msa?: boolean;
  adcsWebEnrollmentHttp?: string;
  adcsWebEnrollmentHttps?: string;
  adcsWebEnrollmentHttpsEpa?: string;
  ldapSigning?: boolean;
  ldapAvailable?: boolean;
  ldapsAvailable?: boolean;
  ldapsepa?: boolean;
  isDc?: boolean;
  isReadOnlyDc?: boolean;
  httpEnrollmentEndpoints?: string;
  httpsEnrollmentEndpoints?: string;
  hasVulnerableEndpoint?: string;

  // NTLM & Authentication Properties
  requireSecuritySignature?: string;
  enableSecuritySignature?: string;
  restrictReceivingNtlmTraffic?: string;
  ntlmMinServerSec?: string;
  ntlmMinClientSec?: string;
  lmCompatibilityLevel?: string;
  useMachineId?: string;
  clientAllowedNtlmServers?: string;
  certificateMappingMethodsRaw?: string;
  certificateMappingMethods?: string;
  strongCertificateBindingEnforcementRaw?: string;
  strongCertificateBindingEnforcement?: string;

  // Additional Properties
  transitive?: string;
  hasUra?: string;
}

export interface CloudResource extends Asset {
  accountRef: number;
  displayName: string;
  resourceGroup: string;
  provider: string;
  region: string;
  resourceType: string;
  ownership: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  properties: Record<string, any>;
}

export const ASSET_KEY_PREFIXES = [
  '#asset#',
  '#addomain#',
  '#adobject#',
  '#aduser#',
  '#adcomputer#',
  '#adgroup#',
  '#adgpo#',
  '#adou#',
  '#adcontainer#',
  '#adlocalgroup#',
  '#adlocaluser#',
  '#adaiaca#',
  '#adrootca#',
  '#adenterpriseca#',
  '#adntauthstore#',
  '#adcerttemplate#',
  '#adissuancepolicy#',
  '#repository#',
  '#integration#',
  '#webapplication#',
  '#awsresource#',
  '#azureresource#',
  '#gcpresource#',
  '#generic#',
];

export interface ADDomain {
  created: string;
  key: string;
  name: string;
  source: string;
  status: AssetStatus;
  ttl: number;
  username: string;
  visited: string;
}

export interface Job {
  asset: Asset;
  name: string;
  key: string;
  delay: number;
  comment?: string;
  source: string;
  dns: string;
  id: string;
  queue: string;
  status: JobStatus;
  ttl: number;
  created: string;
  updated: string;
  username: string;
  config: unknown;
  processed?: number;
  large_artifact_filename?: string;
  s3DownloadURL?: string;
  target?: {
    Target?: {
      username: string;
      key: string;
      source: string;
      dns: string;
      name: string;
      status: string;
      created: string;
      visited: string;
      ttl: number;
      history: EntityHistory[] | null;
      secret: unknown | null;
      comment: string;
      value: string;
    };
  };
}

export interface RiskTemplate {
  key: string;
  name: string;
  comment: string;
  status: RiskCombinedStatus;
  tags?: string[] | null;
}

export type EntityHistory = {
  from: string;
  to: string;
  updated: string;
  by?: string;
  comment: string;
  logit?: number;
  Base?: string;
  filePath?: string;
  affiliationVerdict?: string;
};

export interface ITSMTicket {
  /** ITSM provider identifier (e.g., 'jira', 'azuredevops-workitems', 'freshdesk') */
  provider: string;
  /** Ticket ID / key (e.g., 'PROJ-123', 'AB#456') */
  ticketId: string;
  /** Validated external URL to the ticket */
  link: string;
  /** Current ticket status (e.g., 'Open', 'In Progress', 'Done') */
  status: string;
}

export interface Risk extends RiskTemplate {
  class: string;
  username: string;
  dns: string;
  created: string;
  visited: string;
  updated: string;
  ttl: number;
  source: string; // Legacy field - kept for backward compatibility during migration
  origins?: string[]; // New field - array of origin sources for the vulnerability
  history: EntityHistory[];
  priority: number;
  guid?: string;
  title?: string;
  // Vulnerability enrichment (merged client-side from INSTANCE_OF -> Vulnerability)
  epss?: number;
  cvss?: number;
  kev?: boolean;
  ticket?: ITSMTicket | null;
  exploit?: boolean;
  /** ITSM tickets embedded on the risk from backend */
  tickets?: Array<{
    provider: string;
    id: string;
    link?: string;
    status?: string;
    assignee?: string;
    resolution?: string;
  }>;
  /** Threat intelligence provider metadata */
  ti_provider?: {
    ti_discovery_timestamp?: string;
    ti_last_update_timestamp?: string;
    ti_data?: Record<string, unknown>;
  };
}

export interface ThreatNotificationMetadata {
  praetorianAcknowledged: boolean;
  praetorianScanned: boolean;
  acknowledgedNotified: boolean;
  scannedNotified: boolean;
}

export interface Vulnerability extends ThreatNotificationMetadata {
  username: string;
  key: string;
  id: string;
  epss?: number;
  cvss?: number;
  kev: boolean;
  feed?: string;
  data?: string;
  exploit: boolean;
  writeupId?: number;
  title: string;
  created?: string;
  updated?: string;
  kevDateAdded?: string;
  kevDueDate?: string;
}

export interface Attribute {
  username: string;
  key: string;
  source: string;
  name: string;
  value: string;
  status: string;
  created: string;
  capability: string;
  visited: string;
  ttl: number;
  dns?: string;
  metadata: Record<string, string>;
}

export interface Port {
  key: string;
  username: string;
  source: string;
  capability: string;
  protocol: string;
  port: number;
  service?: string;
  status: string;
  created: string;
  visited: string;
  ttl: number;
}

export interface Preseed extends Attribute {
  status: string;
  subname: string;
  display: string;
  count: number;
  title: string;
  history?: EntityHistory[];
  comment?: string;
  type:
    | 'csp'
    | 'cik'
    | 'favicon'
    | 'tlscert'
    | 'whois+company'
    | 'whois+email'
    | 'whois+name'
    | 'analytics'
    | 'cidr-handle'
    | 'edgar+company';
}

export interface Flag {
  key: string;
  name: string;
  username: string;
}

export interface MyFile {
  key: string;
  name: string;
  updated: string;
  username: string;
  class?: 'report' | 'proof' | 'manual' | 'definition';
}

export interface Statistics {
  risks?: Risk[];
  assets?: Asset[];
  jobs?: Job[];
  attributes?: Attribute[];
  files?: MyFile[];
  conditions?: Condition[];
  seeds?: Seed[];
  count: number;
  statistics: Statistic[];
}

export interface MyResource {
  account: Account[];
  risk: Risk[];
  asset: Asset[];
  addomain: ADDomain[];
  job: Job[];
  attribute: Attribute[];
  port: Port[];
  file: MyFile[];
  condition: Condition[];
  statistic: Statistic[];
  seed: Seed[];
  setting: Setting[];
  preseed: Preseed[];
  flag: Flag[];
  technology: Technology[];
  vulnerability: Vulnerability[];
  webpage: Webpage[];
  webapplication: Asset[];
  credential: Credential[];
  conversation: Conversation[];
  message: Message[];
  configuration: Configuration[];
  repository: Asset[];
  parkeddomain: ParkedDomain[];
  event: TraceEvent[];
  monitoringsession: import('./sections/validations/types').MonitoringSession[];
  monitoredtechnique: import('./sections/validations/types').MonitoredTechnique[];
  monitordetection: import('./sections/validations/types').MonitorDetection[];
}

export interface Technology {
  username: string;
  key: string;
  part: string;
  vendor: string;
  product: string;
  version: string;
  update: string;
  edition: string;
  language: string;
  sw_edition: string;
  target_sw: string;
  target_hw: string;
  other: string;
  cpe: string;
  name: string;
  tags?: string[];
  ttl: number;
  created: string;
  visited: string;
  history: EntityHistory[];
  comment: string;
}

export interface Credential {
  username: string;
  key: string;
  identifier: string;
  name: string;
  type: string;
  category: string;
  format: string[];
  created: string;
  updated: string;
  ttl: number;
  accountKey: string;
  credentialType?: string;
  filename?: string;
  comment: string;
  history: EntityHistory[];
  credentialId: string;
}

export interface Statistic {
  username: string;
  key: string;
  name: string;
  value: string;
  ttl: string;
  created?: string;
  cumulative?: boolean;
  type?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data?: any;
}

export interface AssetsMonitoredCategory {
  total: number;
  category: Record<string, number>;
}

export interface AssetsMonitored {
  cloud: AssetsMonitoredCategory;
  network: AssetsMonitoredCategory;
  code: AssetsMonitoredCategory;
  total: number;
}
export interface ApiKey {
  id: string;
  key: string;
  name: string;
  created: string;
  secret: string;
  deleted: string;
  expires: string;
  role: string;
}

export interface Configuration {
  key?: string;
  last_modified?: string;
  name: string;
  username?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  value: any;
}
export interface GenericResource {
  accounts: Account[];
  risks: Risk[];
  assets: Asset[];
  awsresources: Asset[];
  azureresources: Asset[];
  jobs: Job[];
  attributes: Attribute[];
  files: MyFile[];
  conditions: Condition[];
  seeds: Seed[];
  statistics: Statistic[];
  preseeds: Preseed[];
  flags: Flag[];
  keies: ApiKey[];
  webpages: Webpage[];
  webapplications: Asset[];
  accesss: Access[];
  integrations: Asset[];
  repositories: Asset[];
  addomains: Asset[];
  gcpresources: Asset[];
  configurations: Configuration[];
}

export interface Access {
  email: string;
  key: string;
  role?: string;
  sub: string;
  type: string;
  updated: string;
  username: string;
  value: string;
}

export interface Condition {
  name: string;
  value: string;
  key: string;
  count: number;
}

export type MyResourceKey = keyof MyResource;

export interface BackendStack {
  backend: string;
  clientId: string;
  api: string;
  userPoolId: string;
}

/**
 * User type discriminated union for authorization
 *
 * @remarks
 * Used to determine user permissions and visibility of features:
 * - 'praetorian': Internal Praetorian users with full access
 * - 'customer': Regular customer users with standard access
 * - 'sso-customer': SSO-authenticated customer users
 *
 * This discriminated union provides better type safety than boolean flags,
 * making authorization logic more explicit and easier to extend with new user types.
 */
export type UserType = 'praetorian' | 'customer' | 'sso-customer';

export interface AuthState extends BackendStack {
  me: string;
  sub: string;
  principal: string; // the real human identity (priority order: email > IdP subject ID > Cognito sub)
  isSignedIn: boolean;
  ssoDomain: string;
  whoami?: WhoamiResponse; // populated by /whoami call on login and impersonation changes
}

export interface AuthContextType extends AuthState {
  friend: string;
  userIdFromUrl: string;
  isDemoOrg: boolean;
  isPraetorianUser: boolean; // Keep for backward compatibility
  userType: UserType; // Discriminated union for explicit authorization
  isImpersonating: boolean;
  getToken: () => Promise<string>;
  confirmOTP: (username: string, password: string, opt: string) => void;
  error: string;
  isLoading: boolean;
  login: (username: string, password: string) => void;
  logout: () => Promise<void>;
  setCustomStack: (
    backend?: BackendStack,
    loginCred?: { username: string; password: string }
  ) => void;
  setError: (error: string) => void;
  signup: (username: string, password: string, gotoNext: () => void) => void;
  startImpersonation: (
    memberId: string,
    route?: string,
    disableSwitchingAnimation?: boolean
  ) => Promise<void>;
  stopImpersonation: (
    route?: string,
    disableSwitchingAnimation?: boolean,
    enablePersonalTenancy?: boolean
  ) => Promise<void>;
  signupStepIndex: number;
  setSignupStepIndex: Dispatch<SetStateAction<number>>;
  credentials: { username: string; password: string };
  setCredentials: Dispatch<
    SetStateAction<{ username: string; password: string }>
  >;
  totpSecret: string;
  totpUri: string;
  pendingMfaSignIn: boolean;
  setPendingMfaSignIn: Dispatch<SetStateAction<boolean>>;
  showMFACodeModal: boolean;
  setShowMFACodeModal: Dispatch<SetStateAction<boolean>>;
  setTotpSecretDetails: (totpSetupDetails: {
    sharedSecret: string;
    getSetupUri: (appName: string) => URL;
  }) => Promise<void>;
  verifyMFASetupSignup: (
    code: string,
    navigateToDashboard?: boolean
  ) => Promise<void>;
  setIsLoading: Dispatch<SetStateAction<boolean>>;
  verifyMFASetupLogin: (
    code: string,
    rememberDeviceChecked?: boolean
  ) => Promise<ConfirmSignInOutput>;
}

export enum Integration {
  apiiro = 'apiiro',
  attackiq = 'attackiq',
  axonius = 'axonius',
  censys_asm = 'censys_asm',
  checkmarx = 'checkmarx',
  cycognito = 'cycognito',
  cymulate = 'cymulate',
  safebreach = 'safebreach',
  sailpoint = 'sailpoint',
  hackerone = 'hackerone',
  hook = 'hook',
  webhook = 'webhook',
  servicenow = 'servicenow',
  slack = 'slack',
  splunk = 'splunk',
  email = 'email',
  jira = 'jira',
  confluence = 'confluence',
  github = 'github',
  amazon = 'amazon',
  ns1 = 'ns1',
  gcp = 'gcp',
  azure = 'azure',
  crowdstrike = 'crowdstrike',
  'crowdstrike-flightcontrol' = 'crowdstrike-flightcontrol',
  cortex = 'cortex',
  'cortex-xsiam' = 'cortex-xsiam',
  gitlab = 'gitlab',
  qualys = 'qualys',
  nessus = 'nessus',
  zulip = 'zulip',
  teams = 'teams',
  godaddy = 'godaddy',
  cloudflare = 'cloudflare',
  cloudflarewaf = 'cloudflare-waf',
  fastly = 'fastly',
  flexera = 'flexera',
  sevco = 'sevco',
  shodan = 'shodan',
  sharphound = 'sharphound',
  palo = 'palo',
  orca = 'orca',
  insightvm = 'insightvm',
  invicti = 'invicti',
  jupiterone = 'jupiterone',
  'microsoft-defender' = 'microsoft-defender',
  sentinelone = 'sentinelone',
  akamaidns = 'akamaidns',
  'akamai-waf' = 'akamai-waf',
  aws_waf = 'aws-waf',
  wiz = 'wiz',
  runzero = 'runzero',
  digitalocean = 'digitalocean',
  gchat = 'gchat',
  burpsuite = 'burpsuite',
  panther = 'panther',
  entraid = 'entraid',
  okta = 'okta',
  pingone = 'pingone',
  azuredevops = 'azuredevops',
  'azuredevops-pat' = 'azuredevops-pat',
  'azuredevops-workitems' = 'azuredevops-workitems',
  'azuredevops-workitems-pat' = 'azuredevops-workitems-pat',
  bitbucket = 'bitbucket',
  freshdesk = 'freshdesk',
  freshservice = 'freshservice',
  tenablevm = 'tenablevm',
  vercel = 'vercel',
  xpanse = 'xpanse',
  avalor = 'avalor',
  imperva = 'imperva',
  socradar = 'socradar',
  recordedfuture = 'recordedfuture',
  extrahop = 'extrahop',
  securityscorecard = 'securityscorecard',
  intune = 'intune',
  'seed-import' = 'seed-import',
  'cisa-ch-vulnscan-import' = 'cisa-ch-vulnscan-import',
  'cisa-ch-webappscan-import' = 'cisa-ch-webappscan-import',
  'nsa-csaas-assets-import' = 'nsa-csaas-assets-import',
  'nsa-csaas-vulns-import' = 'nsa-csaas-vulns-import',
  'snyk-apprisk' = 'snyk-apprisk',
  pius_apollo = 'pius_apollo',
  pius_binaryedge = 'pius_binaryedge',
  pius_censys = 'pius_censys',
  pius_crunchbase = 'pius_crunchbase',
  pius_diffbot = 'pius_diffbot',
  pius_dnsdb = 'pius_dnsdb',
  pius_github = 'pius_github',
  pius_opencorporates = 'pius_opencorporates',
  pius_proxycurl = 'pius_proxycurl',
  pius_securitytrails = 'pius_securitytrails',
  pius_shodan = 'pius_shodan',
  pius_viewdns = 'pius_viewdns',
  pius_virustotal = 'pius_virustotal',
  snyk_code = 'snyk-code',
  snyk_dast = 'snyk-dast',
  threatx = 'threatx',
}

export type IntegrationInput = InputsT[number] & {
  dependsOn?: string;
  hideTillDependsOn?: boolean;
  multilineList?: boolean;
  /** Fetch options from connected accounts of this integration type (e.g., 'azure') */
  optionsFromAccounts?: string;
};

export interface IntegrationMeta {
  id: Integration;
  name: string;
  description?: ReactNode;
  logo?: string;
  inputs?: IntegrationInput[];
  overrideMemberName?: string;
  warning?: string;
  message?: React.ReactElement;
  Markup?: React.FC<{ onComplete: () => void; onClose: () => void }>;
  multiple?: boolean;
  validate?: boolean;
  edit?: boolean;
  help?: {
    href: string;
    label: string;
  };
  hideFooter?: boolean;
  /** Hide this integration from the UI (e.g., PAT options hidden by default) */
  hidden?: boolean;
  /** Alternative integration to show as fallback (e.g., PAT instead of Entra ID) */
  fallbackIntegration?: Integration;
  /** Label and description for the fallback link (config-driven) */
  fallbackDescription?: string;
  fallbackLabel?: string;
  /** Child integration that depends on a parent cloud integration (e.g., Azure DevOps using Azure) */
  childIntegration?: boolean;
  /** Marks this integration as a ticketing provider (Jira, Azure DevOps, etc.) */
  ticketing?: boolean;
  /** The attribute name used when storing ticket references on risks */
  ticketAttributeName?: string;
  /** Placeholder text for project search in the configuration step */
  projectSearchPlaceholder?: string;
  configuration?: {
    title: string;
    description: string;
    inputs: IntegrationInput[];
    permissions?: {
      [key: string]: {
        key: string;
        name: string;
        description: string;
        permission: string;
        havePermission: boolean;
      };
    };
  };
  transitions?: {
    title: string;
    description: string;
    requiresField: string;
    disabledTooltip: string;
  };
}

export type JiraProject = {
  key: string;
  name: string;
  issueTypes: {
    name: string;
    subtask: boolean;
  }[];
};

export type JiraTransition = {
  id: string;
  name: string;
  to?: {
    id: string;
    name: string;
  };
};

/** Matches backend TransitionStep: id and name sent in settings (e.g. openToClose/closeToOpen). */
export type JiraTransitionStep = {
  id: string;
  name: string;
};

export type JiraTransitionConfig = {
  openToClose: JiraTransitionStep[];
  closeToOpen: JiraTransitionStep[];
};

export type JiraTransitionsResponse = {
  statuses: JiraTransition[];
  suggestedOpenToClose: JiraTransition[];
  suggestedCloseToOpen: JiraTransition[];
};

export type JiraTemplate = {
  id: string;
  projectKey: string;
  projectName: string;
  issueType: string;
  severities: string;
  autoCreate: string;
  transitions?: JiraTransitionConfig;
  /**
   * Default values for required custom fields on this project's create screen.
   * Discovered via POST /integration/jira/custom-fields and applied to every
   * auto-created ticket whose risk severity meets the configured threshold.
   * Optional — existing templates without this field continue to work unchanged.
   */
  customFieldDefaults?: JiraCustomFieldDefault[];
};

/** Allowed value for a select / multi-select / cascading custom field. */
export type JiraCustomFieldOption = {
  id: string;
  value: string;
  child?: JiraCustomFieldOption[];
};

/** Discovery payload shape — what GET ./custom-fields returns. */
export type JiraCustomField = {
  id: string;
  name: string;
  /** Normalized type: string|number|date|datetime|option|array|cascading|labels|user */
  type: string;
  required: boolean;
  allowedValues?: JiraCustomFieldOption[];
};

export type JiraCustomFieldsResponse = {
  fields: JiraCustomField[];
};

/**
 * Persisted default for a single required custom field. `value` is already
 * shaped per Jira's per-type contract (string for primitives; {value} or {id}
 * for option; array of those for arrays; {value, child:{value}} for cascading)
 * — the backend passes it through to createIssuePayload as-is.
 */
export type JiraCustomFieldDefault = {
  id: string;
  type: string;
  value: unknown;
};

export interface JobFilters {
  query: string;
  search: string;
  failedReason: string;
  riskKey?: string;
}

export interface RiskFilters {
  search: string;
  query: string;
}

export type SeverityOpenCounts = Partial<Record<RiskSeverity, number>>;

export enum ParsedFileTypes {
  IMAGE = 'image',
  PDF = 'pdf',
  DOCUMENT = 'document',
  BINARY = 'binary',
}

export interface TableAsset {
  value: string; // Contains both IP and domain information (separated by #)
  created: string; // First seen date
  visited: string; // Last seen date
  capability?: string; // Optional capability field
  ip?: string;
  domain?: string;
}

export interface Breadcrumb {
  label: string;
  className?: string;
  order: number;
  to?: string;
}

export interface Setting {
  username: string;
  key: string;
  name: string;
  value: string | object;
}

export interface ScanSchedule {
  days: WeekDay[];
  start: { hour: number; minute: number };
  end: { hour: number; minute: number };
  manualBypass?: boolean;
}

export enum WeekDay {
  Sunday = 0,
  Monday = 1,
  Tuesday = 2,
  Wednesday = 3,
  Thursday = 4,
  Friday = 5,
  Saturday = 6,
}

export const WeekDayOptions = [
  {
    label: 'Mon',
    value: WeekDay.Monday,
  },
  {
    label: 'Tue',
    value: WeekDay.Tuesday,
  },
  {
    label: 'Wed',
    value: WeekDay.Wednesday,
  },
  {
    label: 'Thu',
    value: WeekDay.Thursday,
  },
  {
    label: 'Fri',
    value: WeekDay.Friday,
  },
  {
    label: 'Sat',
    value: WeekDay.Saturday,
  },
  {
    label: 'Sun',
    value: WeekDay.Sunday,
  },
];

export enum FlagType {
  DISABLE_ATTACK_SURFACE_MANAGEMENT = 'disable_attack_surface_management',
  DISABLE_VULNERABILITY_MANAGEMENT = 'disable_vulnerability_management',
  ENABLE_BREACH_AND_ATTACK = 'enable_breach_and_attack',
  DISABLE_CYBER_THREAT_INTELLIGENCE = 'disable_cyber_threat_intelligence',
  DISABLE_STATIC_IP_ADDRESSES = 'disable_static_ip_addresses',
  ENABLE_PERSONAL_TENANCY = 'enable_personal_tenancy',
  DISABLE_AIMI_TRAINING = 'disable_aiml_training',
  DISABLE_AI_INFERENCE = 'disable_ai_inference',
  ENABLE_CLOUD_INTEGRATION_OVERRIDE = 'enable_cloud_integration_override',
  ENABLE_CLOUD = 'enable_cloud',
  ENABLE_USERS = 'enable_users',
  ENABLE_DEVICE = 'enable_device',
  ENABLE_EXTERNAL = 'enable_external',
  ENABLE_CODE = 'enable_code',
  ENABLE_AI_AUTOMATION = 'enable_ai_automation',
  ENABLE_AI_AGENTS = 'enable_ai_agents',
  ENABLE_AI_BYOM = 'enable_ai_byom',
  STATIC = 'static',
}

export const ChariotModuleFlags = [
  FlagType.DISABLE_ATTACK_SURFACE_MANAGEMENT,
  FlagType.DISABLE_VULNERABILITY_MANAGEMENT,
  FlagType.ENABLE_BREACH_AND_ATTACK,
  FlagType.DISABLE_CYBER_THREAT_INTELLIGENCE,
  FlagType.DISABLE_STATIC_IP_ADDRESSES,
  FlagType.DISABLE_AIMI_TRAINING,
  FlagType.DISABLE_AI_INFERENCE,
  FlagType.ENABLE_CLOUD_INTEGRATION_OVERRIDE,
  FlagType.ENABLE_AI_AGENTS,
  FlagType.ENABLE_AI_BYOM,
  FlagType.STATIC, // Needed for Static IP toggle state
];

export const STATIC_IP_ADDRESS = '66.45.78.0/24';
export enum KeyboardListersId {
  POE = 'poeTabs',
  EVIDENCE_TAB = 'evidenceTab',
  AFFECTED_ASSETS = 'affectedAssets',
  SEEDS_MAIN_TABS = 'seedsMainTabs',
  RED_TEAM_MAIN_TABS = 'redTeamMainTabs',
  INTEGRATION_DRAWER = 'integrationDrawer',
  PRESEED_DRAWER = 'preseedDrawer',
  NAVIGATION = 'navigation',
}

export enum CrawlerRunType {
  CRAWL = 'crawl',
  LOGIN = 'login',
  DIRBUSTER = 'dirbuster',
}

/**
 * @deprecated Use Webpage typed fields instead
 */
export interface WebpageMetadata {
  'web-secret-identified'?: boolean | string;
  'login-identified'?: boolean | string;
  'parameters-identified'?: boolean | string;
  content_length?: number;
  resources?: string;
  screenshot?: string;
}

export interface Webpage {
  key: string;
  url: string;
  source: string[];
  created: string;
  visited: string;
  // NEW typed fields (promoted from metadata)
  /** Path to screenshot file */
  screenshot?: string;
  /** Path to network resources zip */
  resources?: string;
  /** Fingerprint type: "llm", "authentication", etc. */
  type: string;
  /** Detected service name: "ollama", "openai", "okta", etc. */
  service?: string;
  /**
   * @deprecated Use typed fields (screenshot, resources, endpoint_fingerprint) instead
   */
  metadata: WebpageMetadata;
  state: 'interesting' | 'unanalyzed' | 'uninteresting';
  details_filepath: string;
  status: AssetStatus;
  sso_identified?: Record<
    string,
    {
      last_seen: string;
      id: string;
      name: string;
      original_provider_url: string;
    }
  >;
}

export interface GroupedScanResult {
  id: string;
  sourceString: string;
  port?: string;
  protocol?: string;
  runType?: string;
  timestamp?: number;
  date?: Date;
  totalUrlsFound?: number;
  webpages: Webpage[];
  crawlerZipPath?: string;
  summaryAttribute?: Attribute;
}

/* API Schema Types */
export interface SchemaField {
  type: string;
  filterable: boolean;
}

export interface SchemaType {
  [fieldName: string]: SchemaField;
}

export interface Schema {
  [typeName: string]: SchemaType;
}

export interface AssetWithWebpage extends Asset {
  webpage?: Webpage;
}

export const availableNotifications = [
  Integration.slack,
  Integration.jira,
  Integration.webhook,
  Integration.servicenow,
  Integration.zulip,
  Integration.teams,
  Integration.gchat,
  Integration.email,
];

export type CustomerTypeValue =
  | 'FREEMIUM'
  | 'PILOT'
  | 'ENGAGEMENT'
  | 'MANAGED'
  | 'SAAS'
  | 'BUG_BOUNTY'
  | 'DELETE';

export const CUSTOMER_TYPE_LABELS: Record<CustomerTypeValue, string> = {
  FREEMIUM: 'Freemium',
  PILOT: 'Pilot',
  ENGAGEMENT: 'Project',
  MANAGED: 'Managed',
  SAAS: 'SaaS',
  BUG_BOUNTY: 'Bug Bounty',
  DELETE: 'Delete',
};

export const monitoredAssetsCategoryNameMap: Record<string, string> = {
  digitalocean: 'Digital Ocean',
  digitaloceanResources: 'Digital Ocean Resources',
  ip: 'IP',
  ipv4: 'IPv4',
  ipv6: 'IPv6',
  github: 'GitHub',
  gitlab: 'GitLab',
  bitbucket: 'Bitbucket',
  amazon: 'AWS',
  amazonResources: 'AWS Resources',
  azure: 'Azure',
  azureResources: 'Azure Resources',
  gcp: 'GCP',
  gcpResources: 'GCP Resources',
  domain: 'Domains',
  subdomain: 'Subdomains',
};

/* Chat Types */
export interface TraceEvent {
  key: string;
  username: string;
  trace_id: string;
  span_id: string;
  parent_span_id?: string;
  event_id: string;
  event_type:
    | 'agent_start'
    | 'agent_end'
    | 'job_queued'
    | 'job_start'
    | 'job_end';
  created: string;
  job_key?: string;
  status?: 'Pass' | 'Fail';
  attributes?: Record<string, string>;
  ttl: number;
}

export interface Conversation {
  key: string;
  id: string;
  uuid: string;
  created: string;
  updated?: string;
  username: string;
  title?: string;
  name?: string;
  topic?: string;
  trace_id?: string;
  parent_id?: string;
  status?: 'queued' | 'active' | 'idle' | 'suspended' | '';
}

export interface Message {
  key: string;
  id: string;
  uuid: string;
  conversationId: string;
  role: 'user' | 'chariot' | 'planner-output' | 'tool call' | 'tool response';
  content: string;
  timestamp: string;
  username: string;
  attachments?: string[];
}

export interface ChatMessageRequest {
  conversationId: string;
  message: string;
  mode?: 'query' | 'agent';
}

export interface ChatMessageResponse {
  message: Message;
  processing: boolean;
  error?: string;
}

// Backend API response structure from conversation handler
export interface ConversationResponse {
  conversation?: Conversation;
  response?: {
    conversationId: string;
    response: string;
    success: boolean;
  };
  error?: string;
}
export interface MonitoringCounts {
  cloud: {
    amazon: {
      external: number;
      internal: number;
      resources: number;
    };
    azure: {
      external: number;
      internal: number;
      resources: number;
    };
    gcp: {
      external: number;
      internal: number;
      resources: number;
    };
    digitalocean: {
      external: number;
      internal: number;
      resources: number;
    };
  };
  externalNetwork: {
    ipv4: number;
    ipv6: number;
    domain: number;
    subdomain: number;
  };
  code: {
    github: number;
    gitlab: number;
    bitbucket: number;
    azuredevops: number;
  };
  internalNetwork: {
    ipv4: number;
    ADDomain: number;
    ADUser: number;
    ADComputer: number;
  };
  applications: {
    webapplication: number;
    webseed: number;
    webllm: number;
  };
}

export type MonitoringCategories = keyof MonitoringCounts;

export interface SubscriptionTier {
  id: string;
  displayName: string;
  features: Record<string, Record<string, boolean>>;
}

export interface SubscriptionConfiguration {
  estimatedCost: number;
  msrp?: number; // Auto-calculated MSRP (Praetorian only)
  msrpBreakdown?: {
    fqdnCost: number;
    assetCost: number;
    aevCost: number;
  };
  floorPrice?: number; // Auto-calculated floor price (Praetorian only)
  floorBreakdown?: {
    fqdnCost: number;
    assetCost: number;
    aevCost: number;
  };
  attackSurface: Record<string, boolean>;
  numberOfAssets: number;
  numberOfApplications: number;
  numberOfAEVExercises?: number;
  aiCreditPack?: string;
  aiCreditAnnualCost?: number; // Annual cost of AI credit pack (Praetorian only)
  scanSchedule: string;
  selectedTier: string;
  startDate?: string;
  endDate?: string;
}

/**
 * Entitlement represents a granular permission that can be checked at an endpoint.
 * Matches the backend model.Entitlement values from tabularium.
 */
export type Entitlement =
  | 'read'
  | 'write_assets'
  | 'write_jobs'
  | 'write_files'
  | 'conversation_ai'
  | 'manage_accounts'
  | 'manage_settings'
  | 'manage_integrations'
  | 'praetorian';

/**
 * Response from GET /whoami endpoint.
 * Contains the current user's identity, role, and entitlements.
 */
export interface WhoamiPrincipal {
  name: string;
  display_name: string;
  praetorian: boolean;
  customer_type: string;
  personal_tenancy_enabled: boolean;
  sub: string;
  access_type: string;
  sso_domain?: string;
}

export interface WhoamiTenant {
  name: string;
  display_name: string;
  customer_type: string;
}

export interface WhoamiResponse {
  principal: WhoamiPrincipal;
  tenant: WhoamiTenant | null;
  is_assuming: boolean;
  role: string;
  entitlements: Entitlement[];
  accounts: string[];
}

export enum SeedTypeOption {
  DOMAIN_CIDR = 'asset',
  WEB_APPLICATION = 'webapplication',
  WEB_SERVICE = 'webservice',
}

export interface AddSeedFileObj {
  name: string;
  status: MutationStatus;
  primary_url?: string;
  details_path?: string;
  burp_type?: string;
  error?: string;
}
